import { useState } from "react";
import { UserRound, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import { getProfile, updateProfile, resetProfile } from "@/api/profile";
import { GlassCard } from "@/components/GlassCard";
import { Field, TextInput } from "./fields";
import { showApiError, SkeletonRows, ErrorNote } from "./helpers";
import { LOGO_URL } from "@/config";

export const BotProfile = ({ guildId }) => {
  const { data, loading, error, refetch } = useApi(() => getProfile(guildId));
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const live = data?.profile || {};
  const stored = data?.stored || {};
  const current = form || {
    nickname: stored.nickname ?? live.nickname ?? "",
    bio: stored.bio ?? live.bio ?? "",
    avatar: stored.avatar ?? "",
    banner: stored.banner ?? "",
  };

  const isDirty =
    form &&
    (form.nickname !== (stored.nickname ?? live.nickname ?? "") ||
      form.bio !== (stored.bio ?? live.bio ?? "") ||
      form.avatar !== (stored.avatar ?? "") ||
      form.banner !== (stored.banner ?? ""));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["nickname", "bio", "avatar", "banner"].forEach((k) => {
        payload[k] = current[k] === "" ? null : current[k];
      });
      await updateProfile(guildId, payload);
      toast.success("Bot profile updated on Discord.");
      setForm(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      await resetProfile(guildId);
      toast.success("Bot profile reset to default.");
      setForm(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setResetting(false);
    }
  };

  return (
    <GlassCard className="p-6 md:p-8" hover={false} data-testid="module-profile">
      <div id="profile" className="scroll-mt-40">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03]">
            <UserRound className="h-5 w-5 text-paper-100" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-display text-lg font-semibold text-paper-100">Bot Profile</h3>
            <p className="text-sm text-paper-500">Xevon's live nickname, bio, avatar, and banner in this server — applied directly via Discord.</p>
          </div>
        </div>

        {loading && <div className="mt-6"><SkeletonRows testId="profile-loading" /></div>}
        {!loading && error && <div className="mt-6"><ErrorNote message={error.kind === "offline" || error.kind === "timeout" ? "Unable to connect to Xevon services." : error.message} onRetry={refetch} testId="profile-error" />}</div>}

        {!loading && !error && (
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-5 rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <img
                src={live.avatar_url || LOGO_URL}
                alt="Xevon's current server avatar"
                width={56}
                height={56}
                decoding="async"
                className="h-14 w-14 rounded-full border border-white/15 object-cover"
                data-testid="profile-live-avatar"
              />
              <div className="min-w-0">
                <p className="truncate font-display text-base font-semibold text-paper-100" data-testid="profile-live-name">
                  {live.nickname || "Xevon"}
                </p>
                <p className="truncate text-xs text-paper-500">{live.bio || "No bio set."}</p>
              </div>
              {live.banner_url && (
                <img src={live.banner_url} alt="Current banner" className="ml-auto h-14 w-24 rounded-lg border border-white/10 object-cover" data-testid="profile-live-banner" />
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nickname" hint="Max 32 characters. Empty resets it.">
                <TextInput value={current.nickname} onChange={(v) => setForm({ ...current, nickname: v })} placeholder="Xevon" testId="profile-nickname" />
              </Field>
              <Field label="Bio" hint="Max 190 characters.">
                <TextInput value={current.bio} onChange={(v) => setForm({ ...current, bio: v })} placeholder="Server bio" testId="profile-bio" />
              </Field>
              <Field label="Avatar URL" hint="https:// image URL, or empty to reset.">
                <TextInput value={current.avatar} onChange={(v) => setForm({ ...current, avatar: v })} placeholder="https://…" testId="profile-avatar" />
              </Field>
              <Field label="Banner URL" hint="https:// image URL, or empty to reset.">
                <TextInput value={current.banner} onChange={(v) => setForm({ ...current, banner: v })} placeholder="https://…" testId="profile-banner" />
              </Field>
            </div>

            <div className="flex flex-wrap gap-3 border-t border-white/5 pt-4">
              <button
                onClick={save}
                disabled={!isDirty || saving}
                data-testid="profile-save"
                className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                  isDirty && !saving ? "bg-white text-black hover:scale-[1.02]" : "cursor-not-allowed border border-white/10 text-paper-500"
                }`}
              >
                {saving ? "Saving…" : isDirty ? "Save to Discord" : "Saved"}
              </button>
              <button
                onClick={reset}
                disabled={resetting}
                data-testid="profile-reset"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white/10 disabled:opacity-50"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                {resetting ? "Resetting…" : "Reset to default"}
              </button>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
};
